import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Copy, Send, Loader2, CheckCircle2, Clock, FileText, ShieldCheck } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { DisclosureModal } from "./DisclosureModal";
import { DisclosureEditor } from "./DisclosureEditor";
import { LoadingBlock, ErrorState, Button, Input, Label, Select, Modal, toast } from "@/components/ds";
import { API_BASE } from "@/lib/env";

const INDUSTRIES = [
  { value: "hvac", label: "HVAC" }, { value: "plumbing", label: "Plumbing" },
  { value: "roofing", label: "Roofing" }, { value: "electrical", label: "Electrical" },
];
const PLANS = [{ value: "starter", label: "Starter" }, { value: "growth", label: "Growth" }, { value: "pro", label: "Pro" }];
const INTERVALS = [{ value: "monthly", label: "Monthly" }, { value: "annual", label: "Annual (prepaid 12 months)" }];

const STATUS_TONE: Record<string, string> = {
  pending_payment: "bg-amber-100 text-amber-700", paid: "bg-blue-100 text-blue-700",
  activated: "bg-emerald-100 text-emerald-700",
};

const emptyForm = {
  first_name: "", last_name: "", business_name: "", email: "", phone: "",
  industry: "hvac", city: "", state: "", plan_type: "starter", billing_interval: "monthly",
};

export default function OnboardingAccounts() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [disclosureFor, setDisclosureFor] = useState<{ id: string; name: string } | null>(null);
  const [editWording, setEditWording] = useState(false);
  const me = useQuery({ queryKey: ["internal-me"], queryFn: api.internalMe, retry: false });
  const [f, setF] = useState({ ...emptyForm });
  const { data, isLoading, isError, refetch } = useQuery({ queryKey: ["onboarding-accounts"], queryFn: api.onboardingList });

  const set = (k: string, v: string) => setF((p) => ({ ...p, [k]: v }));

  const createMut = useMutation({
    mutationFn: () => api.onboardingCreate(f),
    onSuccess: (res: any) => {
      // Say what actually happened. Telling a rep the customer was emailed when they were not
      // leaves them waiting on someone who never heard from us.
      if (res.email_status === "sent") toast.success("Account created, secure link emailed to the customer.");
      else toast.error(`Account created, but the email did not send (${res.email_error || res.email_status}). The link is on your clipboard, send it to them.`);
      copyLink(res.activation_path);
      setOpen(false); setF({ ...emptyForm });
      qc.invalidateQueries({ queryKey: ["onboarding-accounts"] });
    },
    onError: (err) => toast.error(formatApiError(err)),
  });

  const resendMut = useMutation({
    mutationFn: (id: string) => api.onboardingResend(id),
    onSuccess: (res: any) => {
      if (res.email_status === "sent") toast.success("New secure link generated and emailed.");
      else toast.error(`New link generated, but the email did not send (${res.email_error || res.email_status}). It is on your clipboard.`);
      copyLink(res.activation_path);
      qc.invalidateQueries({ queryKey: ["onboarding-accounts"] });
    },
    onError: (err) => toast.error(formatApiError(err)),
  });

  const revokeMut = useMutation({
    mutationFn: (id: string) => api.onboardingRevoke(id),
    onSuccess: () => { toast.success("Activation link revoked."); qc.invalidateQueries({ queryKey: ["onboarding-accounts"] }); },
    onError: (err) => toast.error(formatApiError(err)),
  });

  const linkBase = API_BASE || window.location.origin;
  const copyLink = (path?: string | null) => {
    if (!path) return;
    navigator.clipboard?.writeText(`${linkBase}${path}`).then(() => toast.success("Activation link copied to clipboard")).catch(() => {});
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    for (const [k, v] of Object.entries(f)) if (!String(v).trim()) { toast.error("Please complete all fields."); return; }
    createMut.mutate();
  };

  if (isLoading) return <LoadingBlock rows={5} />;
  if (isError) return <ErrorState onRetry={refetch} />;

  const accounts = data?.accounts || [];

  return (
    <div className="space-y-6" data-testid="onboarding-accounts">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">New Customer Onboarding</h1>
          <p className="text-sm text-slate-500">Create an account, then send a secure link so the customer pays by Stripe and sets their own password.</p>
        </div>
        <div className="flex items-center gap-2">
          {me.data?.is_senior_admin && (
            <Button variant="outline" onClick={() => setEditWording(true)} data-testid="onboarding-edit-disclosure">
              <FileText className="mr-1 h-4 w-4" /> Disclosure wording
            </Button>
          )}
          <Button onClick={() => setOpen(true)} data-testid="onboarding-new-btn"><Plus className="mr-1 h-4 w-4" /> New account</Button>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white" data-testid="onboarding-list">
        {accounts.length === 0 ? (
          <p className="px-5 py-10 text-center text-sm text-slate-400">No onboarding accounts yet. Create one to send a secure payment link.</p>
        ) : (
          <table className="w-full text-sm">
            <thead><tr className="border-b border-slate-100 text-left text-xs text-slate-400">
              <th className="px-5 py-2">Business</th><th className="px-5 py-2">Contact</th><th className="px-5 py-2">Plan</th>
              <th className="px-5 py-2">Status</th><th className="px-5 py-2">Rep</th><th className="px-5 py-2 text-right">Link</th></tr></thead>
            <tbody>
              {accounts.map((a: any) => (
                <tr key={a.id} className="border-b border-slate-50" data-testid={`onboarding-row-${a.id}`}>
                  <td className="px-5 py-3 font-medium text-slate-800">{a.business_name}</td>
                  <td className="px-5 py-3 text-slate-500">{a.first_name} · {a.contact_email}</td>
                  <td className="px-5 py-3 text-slate-600">
                    {a.plan?.name || a.plan?.plan_type}
                    <span className="ml-1.5 rounded-full bg-slate-100 px-1.5 py-0.5 text-[11px] text-slate-600">
                      {a.plan?.billing_interval === "annual" ? "Annual" : "Monthly"}
                    </span>
                    {a.plan?.due_today != null && (
                      <div className="text-xs text-slate-400">${Number(a.plan.due_today).toLocaleString(undefined, { minimumFractionDigits: 2 })} due today</div>
                    )}
                  </td>
                  <td className="px-5 py-3">
                    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs ${STATUS_TONE[a.status] || "bg-slate-100 text-slate-600"}`}>
                      {a.status === "activated" ? <CheckCircle2 className="h-3 w-3" /> : a.status === "paid" ? <CheckCircle2 className="h-3 w-3" /> : <Clock className="h-3 w-3" />}
                      {String(a.status).replace(/_/g, " ")}{a.expired && a.status === "pending_payment" ? " · expired" : ""}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-slate-500">
                    {a.sales_rep_name || "—"}
                    {a.activation_email && a.activation_email.status !== "sent" && (
                      <div className="mt-0.5 text-xs text-red-600" data-testid={`onboarding-email-failed-${a.id}`}
                        title={a.activation_email.error || ""}>
                        email not sent, copy the link
                      </div>
                    )}
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex items-center justify-end gap-1">
                      {a.activation_path && (
                        <Button variant="outline" size="sm" onClick={() => copyLink(a.activation_path)} data-testid={`onboarding-copy-${a.id}`}><Copy className="h-3.5 w-3.5" /></Button>
                      )}
                      {a.status === "pending_payment" && (
                        <Button variant={a.disclosure_given ? "outline" : "primary"} size="sm"
                          onClick={() => setDisclosureFor({ id: a.id, name: a.business_name })}
                          data-testid={`onboarding-disclosure-${a.id}`}
                          title={a.disclosure_given ? "Disclosure recorded" : "Read the disclosure before payment"}>
                          {a.disclosure_given ? <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" /> : <><FileText className="mr-1 h-3.5 w-3.5" /> Disclosure</>}
                        </Button>
                      )}
                      {a.status !== "activated" && (
                        <Button variant="outline" size="sm" onClick={() => resendMut.mutate(a.id)} disabled={resendMut.isPending} data-testid={`onboarding-resend-${a.id}`}>
                          {resendMut.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Send className="mr-1 h-3.5 w-3.5" /> Resend</>}
                        </Button>
                      )}
                      {a.status !== "activated" && a.activation_path && (
                        <Button variant="outline" size="sm" onClick={() => revokeMut.mutate(a.id)} disabled={revokeMut.isPending} data-testid={`onboarding-revoke-${a.id}`}>Revoke</Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <DisclosureEditor open={editWording} onOpenChange={setEditWording} />
      <DisclosureModal accountId={disclosureFor?.id ?? null} businessName={disclosureFor?.name}
        open={!!disclosureFor} onOpenChange={(v) => !v && setDisclosureFor(null)} />

      <Modal open={open} onOpenChange={setOpen} title="Create onboarding account">
        <form onSubmit={submit} className="grid gap-3 sm:grid-cols-2" data-testid="onboarding-create-form">
          <div><Label>First name</Label><Input value={f.first_name} onChange={(e) => set("first_name", e.target.value)} data-testid="onb-first" /></div>
          <div><Label>Last name</Label><Input value={f.last_name} onChange={(e) => set("last_name", e.target.value)} data-testid="onb-last" /></div>
          <div className="sm:col-span-2"><Label>Business name</Label><Input value={f.business_name} onChange={(e) => set("business_name", e.target.value)} data-testid="onb-business" /></div>
          <div><Label>Email</Label><Input type="email" value={f.email} onChange={(e) => set("email", e.target.value)} data-testid="onb-email" /></div>
          <div><Label>Phone</Label><Input value={f.phone} onChange={(e) => set("phone", e.target.value)} data-testid="onb-phone" /></div>
          <div><Label>Industry</Label><Select value={f.industry} onChange={(v) => set("industry", v)} testid="onb-industry" options={INDUSTRIES} /></div>
          <div><Label>Plan</Label><Select value={f.plan_type} onChange={(v) => set("plan_type", v)} testid="onb-plan" options={PLANS} /></div>
          <div><Label>Billing</Label><Select value={f.billing_interval} onChange={(v) => set("billing_interval", v)} testid="onb-interval" options={INTERVALS} /></div>
          <div><Label>City</Label><Input value={f.city} onChange={(e) => set("city", e.target.value)} data-testid="onb-city" /></div>
          <div><Label>State</Label><Input value={f.state} onChange={(e) => set("state", e.target.value)} data-testid="onb-state" /></div>
          <div className="mt-2 sm:col-span-2 flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="submit" disabled={createMut.isPending} data-testid="onb-submit">
              {createMut.isPending ? <><Loader2 className="mr-1 h-4 w-4 animate-spin" /> Creating…</> : "Create & send link"}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
